import React, { useState, useEffect } from "react";
import axios from "axios";
import { Send, Building2, UserRound, CheckCircle2, Loader2 } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { CONTACT_INFO } from "../data/mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = ({ defaultRole = "company", prefillSubject = "", roleSignal, subjectSignal }) => {
  const { t, lang } = useLanguage();
  const [role, setRole] = useState(defaultRole);
  const [form, setForm] = useState({
    company_name: "",
    name: "",
    subject: "",
    email: "",
    phone: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  // React to external prefill signals (e.g. clicking Apply on a job)
  useEffect(() => {
    if (roleSignal) setRole(roleSignal);
  }, [roleSignal]);
  useEffect(() => {
    if (subjectSignal) setForm((f) => ({ ...f, subject: subjectSignal }));
  }, [subjectSignal]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/messages`, {
        role,
        lang,
        company_name: form.company_name,
        name: form.name,
        subject: form.subject,
        email: form.email,
        phone: form.phone,
        message: form.message,
      });
      setStatus("success");
      setForm({ company_name: "", name: "", subject: "", email: "", phone: "", message: "" });
      setTimeout(() => setStatus("idle"), 6000);
    } catch (err) {
      console.error(err);
      setStatus("error");
      setTimeout(() => setStatus("idle"), 6000);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/35 outline-none backdrop-blur-md transition-colors focus:border-white/45";
  const labelCls = "mb-2 block text-sm font-medium text-white/75";

  return (
    <section id="contact" className="relative py-24 lg:py-32 scroll-mt-20">
      <div className="container-px mx-auto max-w-5xl">
        <div className="reveal text-center">
          <p className="text-xs font-semibold tracking-[0.3em] text-white/50">{t.contact.eyebrow}</p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white">
            {t.contact.title}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-white/60">{t.contact.subtitle}</p>
        </div>

        <form onSubmit={submit} className="reveal glass-card mt-12 rounded-3xl p-7 sm:p-10">
          {/* Role toggle */}
          <div className="mb-8">
            <span className={labelCls}>{t.contact.iAm}</span>
            <div className="inline-flex rounded-full border border-white/15 bg-white/5 p-1.5">
              <button
                type="button"
                onClick={() => setRole("company")}
                className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-all ${
                  role === "company" ? "bg-white text-black" : "text-white/65 hover:text-white"
                }`}
              >
                <Building2 className="h-4 w-4" /> {t.contact.company}
              </button>
              <button
                type="button"
                onClick={() => setRole("worker")}
                className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-all ${
                  role === "worker" ? "bg-white text-black" : "text-white/65 hover:text-white"
                }`}
              >
                <UserRound className="h-4 w-4" /> {t.contact.worker}
              </button>
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            {role === "company" && (
              <div className="sm:col-span-2">
                <label className={labelCls}>{t.contact.companyName}</label>
                <input className={inputCls} placeholder={t.contact.companyNamePh} value={form.company_name} onChange={update("company_name")} />
              </div>
            )}
            <div>
              <label className={labelCls}>{t.contact.name}</label>
              <input required className={inputCls} placeholder={t.contact.namePh} value={form.name} onChange={update("name")} />
            </div>
            <div>
              <label className={labelCls}>{t.contact.email}</label>
              <input required type="email" className={inputCls} placeholder={t.contact.emailPh} value={form.email} onChange={update("email")} />
            </div>
            <div>
              <label className={labelCls}>{t.contact.phone}</label>
              <input className={inputCls} placeholder={t.contact.phonePh} value={form.phone} onChange={update("phone")} />
            </div>
            <div>
              <label className={labelCls}>{t.contact.subject}</label>
              <input className={inputCls} placeholder={t.contact.subjectPh} value={form.subject} onChange={update("subject")} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>{t.contact.message}</label>
              <textarea required rows={5} className={`${inputCls} resize-none`} placeholder={t.contact.messagePh} value={form.message} onChange={update("message")} />
            </div>
          </div>

          <div className="mt-7 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="submit"
              disabled={status === "sending"}
              className="glass-btn-solid glass-btn px-7 py-3.5 text-sm font-semibold disabled:opacity-60"
            >
              {status === "sending" ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> {t.contact.sending}</>
              ) : (
                <><Send className="h-4 w-4" /> {t.contact.submit}</>
              )}
            </button>

            {status === "success" && (
              <p className="flex items-center gap-2 text-sm font-medium text-white">
                <CheckCircle2 className="h-4 w-4" /> {t.contact.success}
              </p>
            )}
            {status === "error" && (
              <p className="text-sm font-medium text-red-400">{t.contact.error}</p>
            )}
          </div>
        </form>

        <p className="reveal mt-8 text-center text-sm text-white/45">
          {CONTACT_INFO.email} · {CONTACT_INFO.phone}
        </p>
      </div>
    </section>
  );
};

export default Contact;
